'use client'

import { useCallback, useRef, useState } from 'react'

import { useAuthContext } from '@/hooks/use-auth-context'

import { getApiBaseUrl } from '@/lib/api-config'
import { handleApiError } from '@/lib/api-error-handler'
import { getBackendAuthToken } from '@/lib/backend-auth-token'
import type { ContextResource } from '@/lib/chat-types'
import {
  IMPORT_PARTIAL_FAILURE_ERROR_CODE,
  IMPORT_STREAM_TIMEOUT_ERROR_CODE,
} from '@/lib/import-error-codes'

export {
  IMPORT_PARTIAL_FAILURE_ERROR_CODE,
  IMPORT_STREAM_TIMEOUT_ERROR_CODE,
} from '@/lib/import-error-codes'

/** Pixel permission gap detected while importing a Meta account */
export interface PixelWarning {
  /** Pixels the connected identity can't read; pixelId "*" means the whole list is denied */
  inaccessiblePixels: Array<{ pixelId: string }>
  message: string
}

export interface ProviderWarningIssue {
  scope?: string
  endpoint?: string
  reason?: string
  message?: string
  severity?: string
  status_code?: number
  meta_code?: number
  meta_subcode?: number
  fbtrace_id?: string
  checkpoint_url?: string
  pixel_id?: string
}

export interface ProviderWarning {
  inaccessiblePixels: Array<{ pixelId: string }>
  message: string
  issues: ProviderWarningIssue[]
}

/** Account sync progress state */
export interface AccountProgress {
  accountId: string
  accountName: string
  status: 'pending' | 'syncing' | 'done' | 'error'
  action?: 'added' | 'updated' | 'retained' | 'unknown'
  campaignCount?: number
  /**
   * Raw backend error text for the row. Log/debug only — the UI must render
   * the classified `errorCode` copy instead of this string.
   */
  error?: string
  /**
   * Stable classifier code for the row failure (`auth_expired`,
   * `rate_limited`, `provider_down`, ...) when the backend attaches one to the
   * `account_error` frame. Absent on older frames; the UI falls back to the
   * generic row-error copy.
   */
  errorCode?: string
  /** Set when the account's pixels need permission before conversions can sync */
  pixelWarning?: PixelWarning
  /** Set when the provider reported any account-level access/data-read issue */
  providerWarning?: ProviderWarning
}

/** Import summary info */
export interface ImportSummary {
  totalDiscovered: number
  added: number
  updated: number
  retained: number
  removed: number
}

/** Result after import completion */
export interface ImportResult {
  credential: {
    id: number
    name: string
    platform: string
    verification_status: string
  }
  summary: ImportSummary
  importedAccounts: Array<{
    id: number
    resource_type?: 'ad_account' | 'integration_resource'
    platform: string
    account_id: string
    name: string
  }>
  newlyImportedAccounts: Array<{
    id: number
    resource_type?: 'ad_account' | 'integration_resource'
    platform: string
    account_id: string
    name: string
  }>
  removedAccounts: Array<{
    id: number
    resource_type?: 'ad_account' | 'integration_resource'
    platform: string
    account_id: string
    name: string
  }>
  contextResources: ContextResource[]
  /** Whether this is a linking-only operation (all accounts already imported) */
  is_linking_only?: boolean
}

interface ImportJobStarted {
  jobId: string
  status: string
}

interface ConsumeSseResult {
  importJob: ImportJobStarted | null
  lastSeq: number
  terminal: boolean
}

const ASYNC_CONFIRM_MODE_HEADER = 'X-Credential-Confirm-Mode'
const ASYNC_CONFIRM_MODE = 'async-job'

/**
 * Inactivity budget for the async import job event loop: 10 minutes with zero
 * SSE frames of ANY kind means the stream itself is dead (hung connection,
 * silent proxy black hole, reconnect loop that never yields a frame), so stop
 * waiting and surface the timeout state.
 *
 * Every well-formed frame resets the budget — including the tail endpoint's
 * unpersisted keep-alive heartbeats. Persisted-event cadence alone cannot
 * distinguish a slow import from a dead one: a single account's campaign sync
 * has no per-account cap and can legitimately persist nothing for longer than
 * this budget while the Celery job (hard-limited to ~31 minutes) is still
 * making progress. Treating that silence as a stall killed long imports, so
 * only a fully silent connection trips the timeout; detecting a dead worker
 * behind a still-heartbeating tail is the backend's job (terminal job status
 * ends the tail stream).
 */
export const IMPORT_JOB_EVENT_INACTIVITY_TIMEOUT_MS = 10 * 60_000

/** How often the event-loop watchdog re-checks the inactivity budget. */
const IMPORT_JOB_WATCHDOG_INTERVAL_MS = 5_000

/** Thrown when the async import job event loop exhausts the inactivity budget. */
class ImportStreamStalledError extends Error {
  constructor() {
    super('The import stopped reporting progress. Please try again.')
    this.name = 'ImportStreamStalledError'
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

/** Account conflict info */
export interface AccountConflict {
  account_id: string
  account_name: string | null
  existing_credential_id: number
}

/**
 * Parse the terminal `complete` SSE frame into an {@link ImportResult}.
 *
 * Exported so out-of-component consumers (the Sync Widget session bridge and
 * its post-import binding) share the exact parsing the hook uses for its own
 * `result` state, instead of re-implementing the snake_case frame shape.
 */
export function parseImportCompleteFrame(data: unknown): ImportResult {
  const eventData = data as Record<string, unknown>
  const credential = eventData.credential as Record<string, unknown>
  const summary = eventData.summary as Record<string, unknown>
  const importedAccounts = eventData.imported_accounts as Array<
    Record<string, unknown>
  >
  const newlyImportedAccounts = eventData.newly_imported_accounts as Array<
    Record<string, unknown>
  >
  const removedAccounts = eventData.removed_accounts as Array<
    Record<string, unknown>
  >
  const contextResources = eventData.context_resources as ContextResource[]
  const isLinkingOnly = eventData.is_linking_only as boolean | undefined

  const mapAccount = (acc: Record<string, unknown>) => ({
    id: acc.id as number,
    resource_type:
      (acc.resource_type as 'ad_account' | 'integration_resource') ??
      'ad_account',
    platform: acc.platform as string,
    account_id: acc.account_id as string,
    name: acc.name as string,
  })

  return {
    credential: {
      id: credential?.id as number,
      name: credential?.name as string,
      platform: credential?.platform as string,
      verification_status: credential?.verification_status as string,
    },
    summary: {
      totalDiscovered: (summary?.total_discovered as number) || 0,
      added: (summary?.added as number) || 0,
      updated: (summary?.updated as number) || 0,
      retained: (summary?.retained as number) || 0,
      removed: (summary?.removed as number) || 0,
    },
    importedAccounts: importedAccounts?.map(mapAccount) || [],
    newlyImportedAccounts: newlyImportedAccounts?.map(mapAccount) || [],
    removedAccounts: removedAccounts?.map(mapAccount) || [],
    contextResources: contextResources || [],
    is_linking_only: isLinkingOnly,
  }
}

/**
 * Optional per-frame observers. Invoked synchronously from the SSE event
 * loop for BOTH transport modes (legacy inline SSE and async-job tail), for
 * every frame the loop dispatches to internal state. Callbacks are read
 * through a ref, so consumers may pass inline closures; they typically write
 * to a module-level store (e.g. `sync-widget-store`) that outlives this
 * hook's host component — the async-job fetch loop keeps running after
 * unmount, so frames keep flowing to the store after navigation.
 */
export interface CredentialConfirmStreamCallbacks {
  onFrame?: (eventType: string, data: unknown) => void
}

/** State and methods returned by the hook */
export interface UseCredentialConfirmStreamReturn {
  /** Start streaming confirmation */
  confirm: (
    credentialId: number,
    platform: string,
    selectedAccountIds: string[],
    initialAccounts?: Array<{ accountId: string; accountName: string | null }>,
  ) => Promise<void>
  /** Whether loading is in progress */
  isLoading: boolean
  /** Progress for each account */
  progress: AccountProgress[]
  /** Total number of accounts */
  totalAccounts: number
  /** Number of completed accounts */
  completedCount: number
  /** Final result */
  result: ImportResult | null
  /** Error message (raw backend/browser text; prefer mapping `errorCode`) */
  error: string | null
  /**
   * Stable code for the terminal error when one is known: a backend error
   * code from the SSE error frame / HTTP envelope (e.g.
   * `ACCOUNT_NOT_IMPORTABLE`), or one of the client-side codes
   * (`import_partial_failure`, `import_stream_timeout`). Null when the
   * failure has no structured code — callers fall back to `error`.
   */
  errorCode: string | null
  /**
   * Verbose, multi-line error detail surfaced by the backend in non-prod
   * environments only (e.g. per-manager Google Ads 429 text). Null in prod
   * or when the backend did not attach a detail.
   */
  errorDetail: string | null
  /** Account conflict info (when error code is ACCOUNT_ALREADY_IMPORTED) */
  conflictAccount: AccountConflict | null
  /** Reset state */
  reset: () => void
}

/**
 * Hook for streaming credential confirmation and account import via SSE
 *
 * Uses Server-Sent Events to receive import progress in real time
 */
export function useCredentialConfirmStream(
  callbacks?: CredentialConfirmStreamCallbacks,
): UseCredentialConfirmStreamReturn {
  const { getToken } = useAuthContext()

  // Ref-read so inline callback objects don't churn handleEvent's identity.
  const callbacksRef = useRef<CredentialConfirmStreamCallbacks | undefined>(
    callbacks,
  )
  callbacksRef.current = callbacks

  const [isLoading, setIsLoading] = useState(false)
  const [progress, setProgress] = useState<AccountProgress[]>([])
  const [totalAccounts, setTotalAccounts] = useState(0)
  const [completedCount, setCompletedCount] = useState(0)
  const [result, setResult] = useState<ImportResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [errorCode, setErrorCode] = useState<string | null>(null)
  const [errorDetail, setErrorDetail] = useState<string | null>(null)
  const [conflictAccount, setConflictAccount] =
    useState<AccountConflict | null>(null)

  const reset = useCallback(() => {
    setIsLoading(false)
    setProgress([])
    setTotalAccounts(0)
    setCompletedCount(0)
    setResult(null)
    setError(null)
    setErrorCode(null)
    setErrorDetail(null)
    setConflictAccount(null)
  }, [])

  /** Handle SSE events */
  const handleEvent = useCallback((eventType: string, data: unknown) => {
    callbacksRef.current?.onFrame?.(eventType, data)
    const eventData = data as Record<string, unknown>
    const markFirstPendingAsActive = () => {
      setProgress(prev => {
        const hasActiveItem = prev.some(item =>
          ['syncing', 'done', 'error'].includes(item.status),
        )
        if (hasActiveItem) {
          return prev
        }
        const pendingIndex = prev.findIndex(item => item.status === 'pending')
        if (pendingIndex === -1) {
          return prev
        }
        return prev.map((item, index) =>
          index === pendingIndex ? { ...item, status: 'syncing' } : item,
        )
      })
    }

    switch (eventType) {
      case 'fetch_start': {
        const backendTotal = eventData.total_accounts as number | undefined
        if (typeof backendTotal === 'number' && backendTotal > 0) {
          setTotalAccounts(prev => (prev > 0 ? prev : backendTotal))
        }
        markFirstPendingAsActive()
        break
      }

      case 'heartbeat':
        markFirstPendingAsActive()
        break

      case 'init':
        // Backend total excludes structural manager rows that are selected for
        // discovery context but are not imported as leaf accounts.
        setTotalAccounts((eventData.total_accounts as number) || 0)
        break

      case 'account_start':
        setProgress(prev => {
          const accountId = eventData.account_id as string
          // Remove existing item with same ID, add new one with syncing status
          const filtered = prev.filter(p => p.accountId !== accountId)
          return [
            ...filtered,
            {
              accountId,
              accountName: (eventData.account_name as string) || accountId,
              status: 'syncing' as const,
            },
          ]
        })
        break

      case 'account_done': {
        const rawWarning = eventData.pixel_warning as
          | {
              inaccessible_pixels?: Array<{ pixel_id: string }>
              message?: string
            }
          | null
          | undefined
        const rawProviderWarning = eventData.provider_warning as
          | {
              inaccessible_pixels?: Array<{ pixel_id: string }>
              message?: string
              issues?: ProviderWarningIssue[]
            }
          | null
          | undefined
        const pixelWarning: PixelWarning | undefined = rawWarning
          ? {
              inaccessiblePixels: (rawWarning.inaccessible_pixels ?? []).map(
                px => ({
                  pixelId: px.pixel_id,
                }),
              ),
              message: rawWarning.message ?? '',
            }
          : undefined
        const providerWarning: ProviderWarning | undefined = rawProviderWarning
          ? {
              inaccessiblePixels: (
                rawProviderWarning.inaccessible_pixels ?? []
              ).map(px => ({
                pixelId: px.pixel_id,
              })),
              message: rawProviderWarning.message ?? '',
              issues: rawProviderWarning.issues ?? [],
            }
          : undefined
        setProgress(prev =>
          prev.map(p =>
            p.accountId === (eventData.account_id as string)
              ? {
                  ...p,
                  status: 'done' as const,
                  action: eventData.action as AccountProgress['action'],
                  campaignCount:
                    typeof eventData.campaign_count === 'number'
                      ? eventData.campaign_count
                      : undefined,
                  pixelWarning,
                  providerWarning,
                }
              : p,
          ),
        )
        setCompletedCount((eventData.progress as number) || 0)
        break
      }

      case 'account_error':
        setProgress(prev =>
          prev.map(p =>
            p.accountId === (eventData.account_id as string)
              ? {
                  ...p,
                  status: 'error' as const,
                  error: eventData.error as string,
                  errorCode:
                    typeof eventData.error_code === 'string' &&
                    eventData.error_code
                      ? eventData.error_code
                      : undefined,
                }
              : p,
          ),
        )
        setCompletedCount((eventData.progress as number) || 0)
        break

      case 'complete': {
        setResult(parseImportCompleteFrame(eventData))
        break
      }

      case 'error': {
        setError((eventData.message as string) || 'Unknown error')
        const failedAccounts = eventData.failed_accounts
        if (typeof eventData.code === 'string' && eventData.code) {
          setErrorCode(eventData.code)
        } else if (Array.isArray(failedAccounts) && failedAccounts.length > 0) {
          // Per-account failure summary frame (FM18): no backend code yet,
          // but the shape is unambiguous — tag it so the UI can render the
          // partial-failure copy instead of the raw backend sentence.
          setErrorCode(IMPORT_PARTIAL_FAILURE_ERROR_CODE)
        }
        const details = eventData.details as
          | { error_detail?: string }
          | undefined
        if (details?.error_detail) {
          setErrorDetail(details.error_detail)
        }
        break
      }
    }
  }, [])

  const consumeSseResponse = useCallback(
    async (
      response: Response,
      initialSeq = 0,
      onStreamEvent?: () => void,
    ): Promise<ConsumeSseResult> => {
      if (!response.body) {
        throw new Error('Empty stream response')
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let importJob: ImportJobStarted | null = null
      let lastSeq = initialSeq
      let terminal = false

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })

        // Split events by double newline
        const parts = buffer.split('\n\n')
        buffer = parts.pop() || ''

        for (const part of parts) {
          if (!part.trim()) continue

          // Parse SSE event format: event: xxx\ndata: {...}
          const lines = part.split('\n')
          let eventType = 'message'
          let eventData = ''
          let eventSeq: number | null = null

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              eventType = line.slice(7)
            } else if (line.startsWith('id: ')) {
              const parsedSeq = Number.parseInt(line.slice(4), 10)
              eventSeq = Number.isFinite(parsedSeq) ? parsedSeq : null
            } else if (line.startsWith('data: ')) {
              eventData = line.slice(6)
            }
          }

          if (!eventData) continue

          try {
            const data = JSON.parse(eventData)
            // Any well-formed frame proves the stream is alive — persisted
            // job events AND the tail endpoint's unpersisted keep-alive
            // heartbeats. A long single-account campaign sync can persist
            // nothing for longer than the inactivity budget, so liveness
            // must come from connection traffic, not persisted-event
            // cadence; only true stalls (no frames at all for the budget
            // duration) may trip the caller's timeout.
            onStreamEvent?.()
            if (eventType === 'job_started') {
              const payload = data as {
                job_id?: string
                status?: string
              }
              if (payload.job_id) {
                importJob = {
                  jobId: payload.job_id,
                  status: payload.status || 'queued',
                }
              }
              continue
            }
            if (eventSeq !== null && eventSeq > lastSeq) {
              lastSeq = eventSeq
            }
            if (eventType === 'complete' || eventType === 'error') {
              terminal = true
            }
            handleEvent(eventType, data)
          } catch {
            console.warn('Failed to parse SSE event data:', eventData)
          }
        }
      }

      return { importJob, lastSeq, terminal }
    },
    [handleEvent],
  )

  const consumeImportJobEvents = useCallback(
    async (
      baseUrl: string,
      credentialId: number,
      jobId: string,
      token: string | null,
    ): Promise<void> => {
      let lastSeq = 0
      let retryCount = 0
      // Wall-clock time of the last SSE frame observed (or loop start). Any
      // frame counts — including the tail endpoint's unpersisted keep-alive
      // heartbeats — because a healthy job can spend longer than the budget
      // inside one account's campaign sync without persisting anything. Only
      // a connection that produces no frames at all for the whole budget is
      // treated as stalled.
      let lastStreamEventAt = Date.now()
      const budgetExhausted = () =>
        Date.now() - lastStreamEventAt > IMPORT_JOB_EVENT_INACTIVITY_TIMEOUT_MS

      while (true) {
        if (budgetExhausted()) {
          throw new ImportStreamStalledError()
        }

        const eventsUrl = `${baseUrl}/api/credentials/${credentialId}/confirm-stream/${jobId}/events?after=${lastSeq}`
        // A hung tail request can stay open while delivering nothing (silent
        // proxy / TCP black hole), parking the reader forever, so the
        // inactivity watchdog must abort the in-flight request — checking
        // between reconnects is not enough.
        const controller = new AbortController()
        let stalled = false
        const watchdog = setInterval(() => {
          if (budgetExhausted()) {
            stalled = true
            controller.abort()
          }
        }, IMPORT_JOB_WATCHDOG_INTERVAL_MS)

        try {
          const eventsResponse = await fetch(eventsUrl, {
            method: 'GET',
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            signal: controller.signal,
          })

          if (!eventsResponse.ok) {
            throw new Error(`HTTP error ${eventsResponse.status}`)
          }

          const result = await consumeSseResponse(
            eventsResponse,
            lastSeq,
            () => {
              lastStreamEventAt = Date.now()
            },
          )
          lastSeq = result.lastSeq
          if (result.terminal) {
            return
          }
        } catch (err) {
          if (stalled) {
            throw new ImportStreamStalledError()
          }
          throw err
        } finally {
          clearInterval(watchdog)
        }

        retryCount += 1
        await sleep(Math.min(250 * retryCount, 5000))
      }
    },
    [consumeSseResponse],
  )

  const confirm = useCallback(
    async (
      credentialId: number,
      platform: string,
      selectedAccountIds: string[],
      initialAccounts?: Array<{
        accountId: string
        accountName: string | null
      }>,
    ) => {
      reset()
      setIsLoading(true)

      // If initial account info is available, initialize progress state immediately.
      if (initialAccounts && initialAccounts.length > 0) {
        const initialProgress: AccountProgress[] = initialAccounts.map(acc => ({
          accountId: acc.accountId,
          accountName: acc.accountName || acc.accountId,
          status: 'pending',
        }))
        setProgress(initialProgress)
        setTotalAccounts(initialAccounts.length)
        setCompletedCount(0)
      } else {
        // Even without account info, set the total count (based on selectedAccountIds)
        setTotalAccounts(selectedAccountIds.length)
        setCompletedCount(0)
      }

      try {
        const token = await getBackendAuthToken(getToken)
        const baseUrl = getApiBaseUrl()
        const url = `${baseUrl}/api/credentials/${credentialId}/confirm-stream?platform=${platform}`

        // Always request async-job mode: the backend runs every platform's
        // import in a disconnect-safe background job now. The header is a
        // no-op against a new backend (async is unconditional) and still
        // opts an old backend into async for the platforms it supports —
        // consumption below adapts to whichever shape actually arrives, so
        // deploy order between frontend and backend does not matter.
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            [ASYNC_CONFIRM_MODE_HEADER]: ASYNC_CONFIRM_MODE,
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ selected_account_ids: selectedAccountIds }),
        })

        if (!response.ok) {
          let errorData: unknown
          let message = `HTTP error ${response.status}`
          let envelopeErrorCode: string | undefined
          let errorDetails: unknown

          try {
            errorData = await response.json()
            const errorObj = errorData as {
              error?: { code?: string; message?: string; details?: unknown }
            }
            message = errorObj?.error?.message || message
            envelopeErrorCode = errorObj?.error?.code
            errorDetails = errorObj?.error?.details
          } catch {
            // Ignore parse errors
          }

          handleApiError(errorData || { message }, url, response.status)

          if (envelopeErrorCode) {
            setErrorCode(envelopeErrorCode)
          }

          // Check if this is an account conflict error
          if (
            envelopeErrorCode === 'ACCOUNT_ALREADY_IMPORTED' &&
            errorDetails
          ) {
            const details = errorDetails as {
              account_id?: string
              account_name?: string | null
              existing_credential_id?: number
            }
            if (details.account_id && details.existing_credential_id) {
              setConflictAccount({
                account_id: details.account_id,
                account_name: details.account_name || null,
                existing_credential_id: details.existing_credential_id,
              })
              setError(message)
              setIsLoading(false)
              return
            }
          }

          throw new Error(message)
        }

        // Adapt to whatever the backend actually sent, not a platform guess:
        // - a `job_started` frame → async job; tail its persisted events.
        // - no job frame but the stream reached a terminal (complete/error)
        //   frame → an old backend ran the legacy inline import; it is
        //   already done. This branch keeps posthog/stripe working if the
        //   backend has not rolled yet.
        const { importJob, terminal } = await consumeSseResponse(response)
        if (importJob) {
          await consumeImportJobEvents(
            baseUrl,
            credentialId,
            importJob.jobId,
            token,
          )
          return
        }
        if (!terminal) {
          throw new Error(
            'Import stream ended before completion. Please retry.',
          )
        }
      } catch (err) {
        if (err instanceof ImportStreamStalledError) {
          setErrorCode(IMPORT_STREAM_TIMEOUT_ERROR_CODE)
        }
        const message =
          err instanceof Error ? err.message : 'Stream connection failed'
        setError(message)
      } finally {
        setIsLoading(false)
      }
    },
    [consumeImportJobEvents, consumeSseResponse, getToken, reset],
  )

  return {
    confirm,
    isLoading,
    progress,
    totalAccounts,
    completedCount,
    result,
    error,
    errorCode,
    errorDetail,
    conflictAccount,
    reset,
  }
}
